import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Box, Button, MenuItem, Stack, Tooltip } from '@mui/material';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { Control, Controller, Path, useForm } from 'react-hook-form';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import Alert from '@mui/material/Alert';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { operatorAppApiUrl } from '../api/Http.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { useEffect } from 'react';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { css } from '@emotion/css';
import {
    languageOptions,
    languageTranslations,
} from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/CommonOptions.tsx';
import InfoIcon from '@mui/icons-material/Info';
import { useOffstreetTransactionEntryWithoutExitOutlet } from '../offstreet-transactions/OperatorOffstreetTransactionEntryWithoutExit.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

const enum GuestPermitFormFields {
    contractTemplateId = 'contractTemplateId',
    reference = 'reference',
}

interface GuestPermitFormFieldValues extends GuestPermitValidToFieldValues {
    [GuestPermitFormFields.contractTemplateId]: number | null;
    [GuestPermitFormFields.reference]: string | null;
}

export const enum GuestPermitValidToFields {
    validToDate = 'validToDate',
    validToHourOfDay = 'validToHourOfDay',
}

export interface GuestPermitValidToFieldValues {
    [GuestPermitValidToFields.validToDate]: DateTime;
    [GuestPermitValidToFields.validToHourOfDay]: string;
}

interface GuestPermitOption {
    contractTemplateId: number;
    name: Message;
}

interface GuestPermitGenerateResponse {
    entryWithoutExitId: number;
    contractExternalId: string;
}

export function CloudConnectorGenerateGuestPermit() {
    const navigate = useNavigate();
    const urlParams = useParams<{ entryWithoutExitId: string }>();

    const { refetchList } = useOffstreetTransactionEntryWithoutExitOutlet();
    const query = useQuery();
    const entryWithoutExitId = urlParams.entryWithoutExitId
        ? urlParams.entryWithoutExitId
        : null;
    const [guestPermitOptionsState] = useServerFetch<
        GuestPermitOption[],
        {
            entryWithoutExitId: string;
        },
        object
    >(
        ({ entryWithoutExitId }) => ({
            url: `/ui-api/operator-account/cloud-connector/guest-permit/entry-without-exit/${entryWithoutExitId}`,
        }),
        entryWithoutExitId ? { entryWithoutExitId: entryWithoutExitId } : null,
    );

    const [generateGuestPermitState, generateGuestPermit] = useServerWrite<
        GuestPermitFormFieldValues & { entryWithoutExitId: string | null },
        GuestPermitGenerateResponse,
        null
    >(() => ({
        url: `/ui-api/operator-account/cloud-connector/guest-permit/entry-without-exit/${entryWithoutExitId}`,
    }));
    const generationPending =
        generateGuestPermitState.status === RequestStatus.PENDING;

    const { control, handleSubmit, formState, setValue, watch } =
        useForm<GuestPermitFormFieldValues>({
            defaultValues: {
                [GuestPermitFormFields.contractTemplateId]: null,
                [GuestPermitValidToFields.validToDate]: DateTime.now(),
                [GuestPermitValidToFields.validToHourOfDay]: '23:59',
                [GuestPermitFormFields.reference]: null,
            },
            mode: 'onChange',
        });
    // eslint-disable-next-line react-hooks/incompatible-library
    const validToDate = watch(GuestPermitValidToFields.validToDate);

    useServerSuccessEffect(guestPermitOptionsState, data => {
        if (data.length == 1) {
            setValue(
                GuestPermitFormFields.contractTemplateId,
                data[0].contractTemplateId,
                {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                },
            );
        }
    });

    useServerSuccessEffect(generateGuestPermitState, data => {
        refetchList();
        navigate(
            `${generatePath(
                `/offstreet-transactions/entry-without-exit/:entryWithoutExitId/detail/guest-permit-success/:contractExternalId`,
                {
                    entryWithoutExitId: data.entryWithoutExitId.toString(),
                    contractExternalId: data.contractExternalId,
                },
            )}?${query}`,
            { replace: true },
        );
    });
    useServerErrorEffect(generateGuestPermitState, () =>
        navigate(`${generatePath(`error`)}?${query}`),
    );
    const backUrl = `..?${query}`;

    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={backUrl}
            render={() => (
                <>
                    <OperatorAsyncLoadedSection
                        requestState={guestPermitOptionsState}
                        render={guestPermits => (
                            <ParkingPortalLayoutWithHeader
                                title={
                                    <Localized
                                        de="Gastkarte generieren"
                                        fr="Générer carte d'hôte"
                                        it="Crea carta ospite"
                                        en="Generate guest card"
                                    />
                                }
                                backTo={backUrl}
                            >
                                <Alert variant="filled" severity="info">
                                    <Localized
                                        de="Die Gastkarte übernimmt die Einfahrtszeit des Tickets; das Ticket wird annulliert und kann vernichtet werden."
                                        fr="La carte d'hôte reprend l'heure d'entrée du ticket; le ticket est annulé et peut être détruit."
                                        it="La carta ospite riprende l'orario d'entrata del ticket; quest'ultimo viene annullato e può essere distrutto."
                                        en="The guest card reflects the time of entry on the ticket; the latter is cancelled and can be destroyed."
                                    />
                                </Alert>
                                <Box
                                    component="form"
                                    onSubmit={handleSubmit(
                                        (data: GuestPermitFormFieldValues) => {
                                            generateGuestPermit({
                                                ...data,
                                                entryWithoutExitId:
                                                    entryWithoutExitId,
                                            });
                                        },
                                    )}
                                    sx={{
                                        padding: '0 24px 24px 24px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <ReactHookFormTextField
                                        label={
                                            <Localized
                                                de="Typ"
                                                fr="Type"
                                                it="Tipo"
                                                en="Type"
                                            />
                                        }
                                        name={
                                            GuestPermitFormFields.contractTemplateId
                                        }
                                        control={control}
                                        select
                                        readonly={guestPermits.length == 1}
                                        rules={{ required: true }}
                                        sx={{ paddingBottom: '25px' }}
                                    >
                                        {guestPermits.map(guestPermit => (
                                            <MenuItem
                                                key={
                                                    guestPermit.contractTemplateId
                                                }
                                                value={
                                                    guestPermit.contractTemplateId
                                                }
                                            >
                                                <Localized
                                                    {...guestPermit.name}
                                                />
                                            </MenuItem>
                                        ))}
                                    </ReactHookFormTextField>

                                    <Box sx={{ paddingBottom: '25px' }}>
                                        <GuestPermitValidToDateTimeDropdowns
                                            control={control}
                                            validToDate={validToDate}
                                            minDateTime={DateTime.now()}
                                        />
                                    </Box>

                                    <ReactHookFormTextField
                                        label={
                                            <>
                                                <Localized
                                                    de="Interne Bemerkungen"
                                                    fr="Remarques internes"
                                                    it="Osservazioni interne"
                                                    en="Internal remarks"
                                                />
                                                <Tooltip
                                                    title={
                                                        <Localized
                                                            de="Die interne Bemerkungen sind nur hier im Parkingportal sichtbar."
                                                            fr="Les remarques internes sont uniquement visibles ici dans Parkingportal."
                                                            it="Le osservazioni interne sono visibili unicamente qui in Parkingportal."
                                                            en="The internal remarks are only visible here in Parkingportal."
                                                        />
                                                    }
                                                    placement="right"
                                                >
                                                    <InfoIcon
                                                        sx={{
                                                            color: theme =>
                                                                theme.palette
                                                                    .blue.main,
                                                            marginLeft: 1,
                                                        }}
                                                    />
                                                </Tooltip>
                                            </>
                                        }
                                        name={GuestPermitFormFields.reference}
                                        control={control}
                                        select={false}
                                        sx={{ paddingBottom: '25px' }}
                                    />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            onClick={() => navigate(backUrl)}
                                            variant="outlined"
                                            disabled={generationPending}
                                        >
                                            <Localized
                                                de="Abbrechen"
                                                fr="Annuler"
                                                it="Annulla"
                                                en="Cancel"
                                            />
                                        </Button>
                                        <SubmitButton
                                            sx={{ marginLeft: '5px' }}
                                            loading={generationPending}
                                            disabled={!formState.isValid}
                                            label={
                                                <Localized
                                                    de="Generieren"
                                                    fr="Générer"
                                                    it="Generare"
                                                    en="Generate"
                                                />
                                            }
                                        />
                                    </Box>
                                </Box>
                            </ParkingPortalLayoutWithHeader>
                        )}
                    />
                    <Outlet />
                </>
            )}
        />
    );
}

export function GuestPermitValidToDateTimeDropdowns<
    T extends GuestPermitValidToFieldValues,
>({
    control,
    validToDate,
    minDateTime,
}: {
    control: Control<T>;
    validToDate: DateTime;
    minDateTime: DateTime;
}) {
    const timeSelectionOptions = getTimeSelectionOptions(
        minDateTime,
        validToDate.hasSame(minDateTime, 'day'),
    );

    return (
        <Stack
            spacing={2}
            direction={'row'}
            sx={{
                display: 'flex',
                alignItems: 'baseline',
            }}
        >
            <Controller
                name={GuestPermitValidToFields.validToDate as Path<T>}
                control={control}
                render={({ field }) => (
                    <DesktopDatePicker
                        format="dd.MM.yyyy"
                        label={
                            <Localized
                                de="Gültig bis"
                                fr="Valide jusqu'à"
                                it="Valido fino a"
                                en="Valid Until"
                            />
                        }
                        value={field.value}
                        onChange={field.onChange}
                        minDate={minDateTime.plus({ minute: 1 })}
                        maxDate={DateTime.now().plus({
                            months: 1,
                        })}
                    />
                )}
                rules={{ required: true }}
            />

            <ReactHookFormTextField
                name={GuestPermitValidToFields.validToHourOfDay as Path<T>}
                control={control}
                select
                rules={{ required: true }}
                sx={{ width: '100px' }}
                slotProps={{
                    select: {
                        MenuProps: {
                            slotProps: {
                                paper: {
                                    style: {
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                    },
                                },
                            },
                        },
                    },
                }}
            >
                {timeSelectionOptions.map(hour => (
                    <MenuItem key={hour} value={hour}>
                        {hour}
                    </MenuItem>
                ))}
            </ReactHookFormTextField>
        </Stack>
    );
}

function getTimeSelectionOptions(time: DateTime, sameDay: boolean): string[] {
    const timeOptions = [
        '01:00',
        '02:00',
        '03:00',
        '04:00',
        '05:00',
        '06:00',
        '07:00',
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
        '19:00',
        '20:00',
        '21:00',
        '22:00',
        '23:00',
        '23:59',
    ];
    if (!sameDay) {
        return timeOptions;
    }
    return timeOptions.filter(o => o > time.toFormat('HH:mm'));
}

export function guestPermitReceiptUrl(
    operatorId: number,
    contractExternalId: string | null,
) {
    return operatorAppApiUrl(
        `/${operatorId}/product/${contractExternalId}/guest-permit-pdf`,
    );
}

export function GuestPermitGenerationSuccessModal() {
    const navigate = useNavigate();
    const query = useQuery();
    const { currentLogin, operatorExternalId } = useOperatorContext();
    const { contractExternalId } = useParams<{ contractExternalId: string }>();

    const downloadUrl = guestPermitReceiptUrl(
        currentLogin.mandantId,
        contractExternalId ?? null,
    );

    useEffect(() => {
        openNewTab(urlWithOperatorExternalId(downloadUrl, operatorExternalId));
    }, [downloadUrl, operatorExternalId]);

    return (
        <ConfirmationDialog
            open
            title={
                <Localized
                    de="Gastkarte erstellt"
                    fr="Carte d'hôte créée"
                    it="Carta ospite creata"
                    en="Guest card created"
                />
            }
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            onConfirm={() => navigate(`..?${query}`)}
            dialogVersion={ConfirmationDialogVersions.SUCCESS}
        >
            <div>
                <p>
                    <Localized
                        de="Die Gastkarte wurde erfolgreich erstellt und das zugehörige Ticket wurde annulliert."
                        fr="La carte d'hôte a été créée avec succès et le ticket associé a été annulé."
                        it="La carta ospite è stata generata con successo e il relativo ticket è stato annullato."
                        en="The guest card was successfully created and the associated ticket was cancelled."
                    />
                </p>
                <p>
                    <Localized
                        de="Der Download wird automatisch gestartet. Sollte dies nicht funktionieren oder Sie eine andere Sprache wünschen, können Sie den Download hier unten starten."
                        fr="Le téléchargement démarre automatiquement. Si cela ne fonctionne pas ou si vous avez besoin d'une autre langue, vous pouvez le démarrer ci-dessous."
                        it="Il download inizierà automaticamente. Se non dovesse funzionare o se desidera una lingua diversa, può avviare il download qui sotto."
                        en="Der Download wird automatisch gestartet. Sollte dies nicht funktionieren oder brauchen Sie eine andere Sprache, können Sie ihn hier unten starten."
                    />
                </p>
                <div className={css({ display: 'flex', gap: '10px' })}>
                    {languageOptions.map(language => (
                        <a
                            key={language.value}
                            href={urlWithOperatorExternalId(
                                downloadUrl + '?language=' + language.value,
                                operatorExternalId,
                            )}
                            target="_blank"
                            download
                            rel="noreferrer"
                            className={css({
                                textDecoration: 'underline',
                            })}
                        >
                            <Localized
                                {...languageTranslations(language.value)}
                            />
                        </a>
                    ))}
                </div>
            </div>
        </ConfirmationDialog>
    );
}

export function GuestPermitGenerationErrorModal() {
    const navigate = useNavigate();
    const query = useQuery();
    return (
        <ConfirmationDialog
            open
            title={
                <Localized
                    de="Gastkarte erstellen fehlgeschlagen"
                    fr="Échec de la création de la carte d'hôte"
                    it="Creazione della carta ospite non riuscita"
                    en="Guest card creation failed"
                />
            }
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            onConfirm={() => navigate(`..?${query}`)}
            dialogVersion={ConfirmationDialogVersions.ERROR}
        >
            <p>
                <Localized
                    de="Ein unerwarteter Fehler ist aufgetreten."
                    fr="Une erreur inattendue s'est produite."
                    it="Si è verificato un errore imprevisto."
                    en="An unexpected error has occurred."
                />
            </p>
        </ConfirmationDialog>
    );
}
